import { useState, useMemo } from "react";
import {
  MKButton,
  MKCommand,
  MKCommandList,
  MKCommandEmpty,
  MKCommandGroup,
  MKCommandInput,
  MKCommandItem,
  MKPopover,
  MKPopoverContent,
  MKPopoverTrigger,
  MKScrollArea,
  cn,
} from "@mytraai/mytrakit";
import { Check, ChevronsUpDown } from "lucide-react";

interface SearchSelectProps {
  value: string;
  options: string[];
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  disabled?: boolean;
  className?: string;
  onValueChange: (value: string) => void;
}

export function SearchSelect({
  value,
  options,
  placeholder = "Select...",
  searchPlaceholder = "Search...",
  emptyMessage = "No results found",
  disabled = false,
  className,
  onValueChange,
}: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Filter options manually to preserve original order
  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const searchLower = search.toLowerCase();
    return options.filter((option) =>
      option.toLowerCase().includes(searchLower),
    );
  }, [options, search]);

  return (
    <MKPopover open={open} onOpenChange={setOpen}>
      <MKPopoverTrigger asChild>
        <MKButton
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className={cn("w-full justify-between", className)}
          disabled={disabled}
        >
          <span className={value ? "" : "text-content-tertiary"}>
            {value || placeholder}
          </span>
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </MKButton>
      </MKPopoverTrigger>
      <MKPopoverContent
        className="w-[--radix-popover-trigger-width] overflow-hidden p-0"
        align="start"
      >
        <MKCommand loop shouldFilter={false}>
          <MKCommandInput
            placeholder={searchPlaceholder}
            value={search}
            onValueChange={setSearch}
          />
          <MKScrollArea className="max-h-[200px]">
            <MKCommandList className="overflow-visible">
              <MKCommandEmpty>{emptyMessage}</MKCommandEmpty>
              <MKCommandGroup>
                {filteredOptions.map((option) => (
                  <MKCommandItem
                    key={option}
                    value={option}
                    onSelect={(selectedValue) => {
                      onValueChange(
                        selectedValue === value ? "" : selectedValue,
                      );
                      setOpen(false);
                      setSearch("");
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === option ? "opacity-100" : "opacity-0",
                      )}
                    />
                    {option}
                  </MKCommandItem>
                ))}
              </MKCommandGroup>
            </MKCommandList>
          </MKScrollArea>
        </MKCommand>
      </MKPopoverContent>
    </MKPopover>
  );
}
